import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Home as HomeIcon, Sparkles } from "lucide-react";

/**
 * 404 catch-all page.
 * - Sets `robots: noindex, nofollow` so Google doesn't index unknown URLs
 *   (prevents "soft 404" penalties on the SPA where every route returns HTTP 200).
 * - Gives the user a warm, on-brand off-ramp back to the funnel.
 */
export default function NotFound() {
  useEffect(() => {
    // Title
    document.title = "Page not found · Mind Body Spirit HYGIENE™ Tools";

    // noindex/nofollow — the critical SEO signal for unknown URLs
    const ensureMeta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute("name", name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    // Save the previous robots value so we can restore it when navigating away
    const prevRobots = document.querySelector('meta[name="robots"]');
    const prevRobotsContent = prevRobots ? prevRobots.getAttribute("content") : null;
    ensureMeta("robots", "noindex, nofollow");
    ensureMeta("description", "This page does not exist. Head back home or grab the free 12-month calendar.");

    return () => {
      // Restore prior robots value when leaving this page
      const cur = document.querySelector('meta[name="robots"]');
      if (cur && prevRobotsContent != null) cur.setAttribute("content", prevRobotsContent);
    };
  }, []);

  return (
    <div className="min-h-screen bg-[#042F2E] text-white flex items-center justify-center px-6" data-testid="not-found-page">
      <div className="max-w-xl w-full text-center py-20">
        <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur rounded-full px-4 py-1.5 text-xs uppercase tracking-widest text-[#7BC8CB] mb-6">
          <Sparkles className="w-3 h-3" /> 404 · Not found
        </div>

        <h1 className="font-serif text-4xl sm:text-5xl leading-tight mb-4" data-testid="not-found-headline">
          This path is quiet.
        </h1>
        <p className="text-white/80 text-base leading-relaxed mb-10 max-w-md mx-auto">
          The page you were looking for isn&apos;t here. That&apos;s okay — the doorway home is right below.
          Take a breath. Choose the next honest step.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link
            to="/start"
            className="inline-flex items-center justify-center gap-2 bg-[#7BC8CB] hover:bg-white text-[#042F2E] font-bold rounded-full px-7 py-3.5 text-sm transition-all shadow-lg hover:-translate-y-0.5"
            data-testid="not-found-cta-start"
          >
            <Sparkles className="w-4 h-4" /> Start here — free calendar
          </Link>
          <Link
            to="/"
            className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white/30 hover:border-white/70 text-white font-medium rounded-full px-7 py-3.5 text-sm transition-all"
            data-testid="not-found-cta-home"
          >
            <HomeIcon className="w-4 h-4" /> Home
          </Link>
        </div>

        <div className="mt-14 text-white/50 text-xs">
          <Link to="/blog" className="hover:text-white inline-flex items-center gap-1" data-testid="not-found-cta-blog">
            <ArrowLeft className="w-3 h-3" /> Read the blog instead
          </Link>
        </div>
      </div>
    </div>
  );
}
